import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "@/i18n";
import { useSwipe } from "@/hooks/useSwipe";
import { useContent } from "@/content/ContentContext";

const INTERVAL = 7000;
const EASE = [0.22, 1, 0.36, 1];

const SaporiSlider = ({ title, intro }) => {
  const { t } = useLang();
  const slides = useContent().images.sapori;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const step = (dir) => setIndex((i) => (i + dir + slides.length) % slides.length);
  const swipe = useSwipe(() => step(1), () => step(-1));

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % slides.length), INTERVAL);
    return () => clearInterval(id);
  }, [paused, index, slides.length]);

  return (
    <div
      className="relative min-h-[80svh] md:min-h-[88svh] overflow-hidden flex items-center justify-center text-center touch-pan-y"
      data-testid="sapori-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      {...swipe}
    >
      <AnimatePresence initial={false}>
        <motion.img
          key={slides[index]}
          src={slides[index]}
          alt=""
          loading="lazy"
          draggable="false"
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1.02 }}
          exit={{ opacity: 0, scale: 1, transition: { duration: 2.6, ease: [0.45, 0, 0.55, 1] } }}
          transition={{
            opacity: { duration: 2.6, ease: [0.45, 0, 0.55, 1] },
            scale: { duration: INTERVAL / 1000 + 3, ease: "linear" },
          }}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid={`sapori-slide-${index}`}
          data-edit-list="sapori"
          data-edit-index={index}
        />
      </AnimatePresence>
      <div className="absolute inset-0 bg-olive/70 mix-blend-multiply" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-b from-anthracite/40 via-transparent to-anthracite/50" aria-hidden="true" />

      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 py-24 flex flex-col items-center">
        <p className="eyebrow text-sand mb-8 justify-center">{t.sapori.label}</p>
        <h2
          className="font-serif text-ivory text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight"
          data-testid="sapori-title"
        >
          {title}
        </h2>
        <p
          className="mt-8 md:mt-10 max-w-2xl text-base md:text-lg font-light leading-relaxed text-ivory/85"
          data-testid="sapori-intro"
        >
          {intro}
        </p>
        <div className="mt-12 flex items-center gap-4" data-testid="sapori-slider-controls">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`${i + 1}`}
              data-testid={`sapori-slider-dot-${i}`}
              className="relative h-px w-10 bg-ivory/30 overflow-hidden"
            >
              {i === index && (
                <motion.span
                  key={`${index}-${paused}`}
                  className="absolute inset-y-0 left-0 w-full bg-sand origin-left"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: paused ? 0 : 1 }}
                  transition={{ duration: INTERVAL / 1000, ease: "linear" }}
                />
              )}
              {i < index && <span className="absolute inset-0 bg-ivory/60" />}
            </button>
          ))}
          <span className="ml-2 text-[11px] uppercase tracking-[0.3em] text-ivory/60 tabular-nums" data-testid="sapori-slider-counter">
            {String(index + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SaporiSlider;
